import { ArrowDownToLine, ArrowUpFromLine, Plus } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaTabs } from "@/shared/components/navigation/ColocaTabs";
import useTranslations from "@/shared/lang/useTranslations";

import { BuyHistoryPanel } from "../components/orders/BuyHistoryPanel";
import { SellHistoryPanel } from "../components/sales/SellHistoryPanel";
import {
  CRYPTO_SELL_PATH,
  CRYPTO_SWAPS_PATH,
  HISTORY_TAB_PARAM,
  ORDER_PARAM,
  SALE_PARAM,
  type CryptoHistoryTab,
} from "../utils/cryptoSwapLinks";

/**
 * `/app/crypto-swaps/history` — purchases and sales, one tab each. The tab lives in `?tab=` so a sale page
 * can link straight back to its list; switching tabs drops the other tab's open record.
 */
export default function CryptoSwapHistoryPage() {
  const { t } = useTranslations("cryptoSwaps");
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: CryptoHistoryTab = searchParams.get(HISTORY_TAB_PARAM) === "sells" ? "sells" : "buys";

  useAppShellBreadcrumb(
    useMemo(
      () => [{ label: t("page_breadcrumb_wallets"), to: "/app/wallets" }, { label: t("history_hub_title") }],
      [t],
    ),
  );

  const selectTab = useCallback(
    (next: string) => {
      setSearchParams(
        (params) => {
          if (next === "sells") params.set(HISTORY_TAB_PARAM, "sells");
          else params.delete(HISTORY_TAB_PARAM);
          params.delete(ORDER_PARAM);
          params.delete(SALE_PARAM);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <div className="flex flex-col gap-6 pb-16">
      <ColocaPageHeader
        title={t("history_hub_title")}
        description={t("history_hub_description")}
        actions={
          can("payments:write") ? (
            <>
              <ColocaButton variant="default" icon={Plus} render={<Link to={CRYPTO_SELL_PATH} />}>
                {t("sell_page_new")}
              </ColocaButton>
              <ColocaButton variant="primary" icon={Plus} render={<Link to={CRYPTO_SWAPS_PATH} />}>
                {t("page_new_order")}
              </ColocaButton>
            </>
          ) : undefined
        }
      />

      <ColocaTabs
        value={tab}
        onValueChange={selectTab}
        variant="line"
        listClassName="w-full max-w-sm"
        tabs={[
          {
            value: "buys",
            label: t("history_tab_buys"),
            icon: ArrowDownToLine,
            content: <BuyHistoryPanel />,
          },
          {
            value: "sells",
            label: t("history_tab_sells"),
            icon: ArrowUpFromLine,
            content: <SellHistoryPanel />,
          },
        ]}
        contentClassName="pt-6"
      />
    </div>
  );
}
